import type {
  DocsDocument,
  DocsDocumentSummary,
  DocsPublishResult,
  DocsRevisionListItem,
  DocsSearchItem,
} from '@/bridge/types';

const apiBase = (import.meta.env.VITE_DOCS_API_BASE_URL || '/api/v1').replace(
  /\/$/,
  '',
);
const authCookieKey =
  import.meta.env.VITE_DATART_AUTH_COOKIE_KEY || 'AUTHORIZATION_TOKEN';

interface ApiEnvelope<T> {
  success?: boolean;
  data?: T;
  message?: string;
  errCode?: number | string;
}

export interface DocumentSession {
  authenticated: boolean;
  admin: boolean;
  username?: string;
}

export interface DocsDirectorySummary {
  path: string[];
  sortOrder?: number;
}

export class DocsApiError extends Error {
  readonly code?: number | string;

  constructor(message: string, code?: number | string) {
    super(message);
    this.name = 'DocsApiError';
    this.code = code;
  }
}

function readCookie(name: string) {
  const prefix = `${encodeURIComponent(name)}=`;
  const raw = document.cookie
    .split(';')
    .map(item => item.trim())
    .find(item => item.startsWith(prefix))
    ?.slice(prefix.length);
  if (!raw) return '';
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function persistAuthorization(token: string) {
  const attributes = [
    `${encodeURIComponent(authCookieKey)}=${encodeURIComponent(token)}`,
    'Path=/',
    'SameSite=Lax',
  ];
  const configuredDomain = import.meta.env.VITE_DATART_AUTH_COOKIE_DOMAIN;
  const cookieDomain =
    configuredDomain ||
    (/(^|\.)honeymi\.com\.cn$/i.test(window.location.hostname)
      ? '.honeymi.com.cn'
      : '');
  if (cookieDomain) {
    attributes.push(`Domain=${cookieDomain}`);
  }
  if (window.location.protocol === 'https:') {
    attributes.push('Secure');
  }
  document.cookie = attributes.join('; ');
}

async function request<T>(path: string, init: RequestInit = {}) {
  const headers = new Headers(init.headers);
  const token = readCookie(authCookieKey);
  if (token) {
    headers.set('Authorization', token);
  }
  if (init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${apiBase}${path}`, {
    ...init,
    headers,
    credentials: 'same-origin',
  });
  const refreshedToken = response.headers.get('authorization');
  if (refreshedToken) {
    persistAuthorization(refreshedToken);
  }

  const contentType = response.headers.get('content-type') || '';
  const payload = contentType.includes('application/json')
    ? ((await response.json()) as ApiEnvelope<T>)
    : undefined;
  if (!response.ok || payload?.success === false) {
    throw new DocsApiError(
      payload?.message || `请求失败（${response.status}）`,
      payload?.errCode,
    );
  }
  if (!payload || payload.data === undefined) {
    throw new DocsApiError('文档服务返回了无效数据');
  }
  return payload.data;
}

function jsonBody(value: unknown) {
  return JSON.stringify(value);
}

export const docsApi = {
  getSession() {
    return request<DocumentSession>('/documents/session');
  },

  listDocuments() {
    return request<DocsDocumentSummary[]>('/documents');
  },

  listRevisions(documentId: string) {
    return request<DocsRevisionListItem[]>(
      `/documents/${encodeURIComponent(documentId)}/revisions`,
    );
  },

  deleteRevision(documentId: string, revisionId: string) {
    return request<boolean>(
      `/documents/${encodeURIComponent(documentId)}/revisions/${encodeURIComponent(
        revisionId,
      )}`,
      { method: 'DELETE' },
    );
  },

  getDocument(documentId: string, revisionId?: string) {
    const query = revisionId
      ? `?revisionId=${encodeURIComponent(revisionId)}`
      : '';
    return request<DocsDocument>(
      `/documents/${encodeURIComponent(documentId)}${query}`,
    );
  },

  searchDocuments(params: {
    query: string;
    documentId?: string;
    limit?: number;
  }) {
    const query = new URLSearchParams({ query: params.query });
    if (params.documentId) query.set('documentId', params.documentId);
    if (params.limit) query.set('limit', String(params.limit));
    return request<DocsSearchItem[]>(`/documents/search?${query}`);
  },

  createDocument(params: {
    title: string;
    breadcrumb: string[];
    blocks?: unknown[];
  }) {
    return request<DocsDocument>('/documents', {
      method: 'POST',
      body: jsonBody({
        ...params,
        blocks: params.blocks || [{ type: 'paragraph', content: '' }],
      }),
    });
  },

  updateDocument(
    documentId: string,
    params: { title: string; breadcrumb?: string[] },
  ) {
    return request<DocsDocument>(
      `/documents/${encodeURIComponent(documentId)}`,
      { method: 'PUT', body: jsonBody(params) },
    );
  },

  saveDraft(
    documentId: string,
    baseRevisionId: string,
    latestRevisionId: string,
    blocks: unknown[],
  ) {
    return request<DocsDocument>(
      `/documents/${encodeURIComponent(documentId)}/draft`,
      {
        method: 'PUT',
        body: jsonBody({ baseRevisionId, latestRevisionId, blocks }),
      },
    );
  },

  publish(documentId: string, revisionId: string) {
    return request<DocsPublishResult>(
      `/documents/${encodeURIComponent(documentId)}/publish`,
      { method: 'POST', body: jsonBody({ revisionId }) },
    );
  },

  unpublish(documentId: string, revisionId: string) {
    return request<DocsDocument>(
      `/documents/${encodeURIComponent(documentId)}/unpublish`,
      { method: 'POST', body: jsonBody({ revisionId }) },
    );
  },

  uploadAsset(documentId: string, file: File, imageOnly = false) {
    const formData = new FormData();
    formData.append('file', file);
    return request<{ url: string; mimeType?: string; size?: number }>(
      `/documents/${encodeURIComponent(documentId)}/${
        imageOnly ? 'images' : 'assets'
      }`,
      { method: 'POST', body: formData },
    );
  },

  listDirectories() {
    return request<DocsDirectorySummary[]>('/documents/directories');
  },

  createDirectory(path: string[]) {
    return request<boolean>('/documents/directories', {
      method: 'POST',
      body: jsonBody({ path }),
    });
  },

  renameDirectory(path: string[], name: string) {
    return request<number>('/documents/directories', {
      method: 'PUT',
      body: jsonBody({ path, name }),
    });
  },

  deleteDirectory(path: string[]) {
    return request<number>('/documents/directories', {
      method: 'DELETE',
      body: jsonBody({ path }),
    });
  },

  moveDirectory(
    path: string[],
    targetPath: string[],
    beforePath?: string[],
  ) {
    return request<boolean>('/documents/directories/position', {
      method: 'PUT',
      body: jsonBody({ path, targetPath, beforePath }),
    });
  },

  moveDocument(
    documentId: string,
    targetPath: string[],
    beforeDocumentId?: string,
  ) {
    return request<boolean>(
      `/documents/${encodeURIComponent(documentId)}/position`,
      {
        method: 'PUT',
        body: jsonBody({ targetPath, beforeDocumentId }),
      },
    );
  },

  archiveDocument(documentId: string) {
    return request<boolean>(`/documents/${encodeURIComponent(documentId)}`, {
      method: 'DELETE',
    });
  },

  importDocument(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return request<DocsDocument>('/documents/import', {
      method: 'POST',
      body: formData,
    });
  },

  async downloadDocument(documentId: string) {
    const headers = new Headers();
    const token = readCookie(authCookieKey);
    if (token) headers.set('Authorization', token);
    const response = await fetch(
      `${apiBase}/documents/${encodeURIComponent(documentId)}/export`,
      { headers, credentials: 'same-origin' },
    );
    const refreshedToken = response.headers.get('authorization');
    if (refreshedToken) {
      persistAuthorization(refreshedToken);
    }
    const contentType = response.headers.get('content-type') || '';
    if (!response.ok || !contentType.includes('application/zip')) {
      let message = `导出失败（${response.status}）`;
      if (contentType.includes('application/json')) {
        try {
          const payload = (await response.json()) as ApiEnvelope<unknown>;
          message = payload.message || message;
        } catch {
          // Keep the status-based fallback for malformed error responses.
        }
      }
      throw new DocsApiError(message);
    }
    const disposition = response.headers.get('content-disposition') || '';
    const encodedFileName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
    let fileName: string | undefined;
    if (encodedFileName) {
      try {
        fileName = decodeURIComponent(encodedFileName.replace(/\+/g, '%20'));
      } catch {
        fileName = undefined;
      }
    }
    return {
      blob: await response.blob(),
      fileName,
      externalAssetCount: Number(
        response.headers.get('X-Document-External-Assets') || 0,
      ),
    };
  },
};
